<template>
  <Dialog
    v-model:visible="visible"
    modal
    :header="isEdit ? 'Edit Retailer Development Actions' : 'New Retailer Development Actions'"
    :style="{ width: '90vw', maxWidth: '700px' }"
    :breakpoints="{ '960px': '75vw', '640px': '95vw' }"
    :dismissableMask="true"
  >
    <template #header>
      <div class="flex items-center gap-2 sm:gap-3">
        <div
          class="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0"
          :style="{
            backgroundColor: form.active ? 'var(--p-mint-300)' : '#94a3b820',
            color: form.active ? 'var(--p-mint-500)' : '#94a3b8',
          }"
        >
          <i class="pi pi-folder text-base sm:text-lg"></i>
        </div>
        <div class="min-w-0 flex-1">
          <div class="font-bold text-base sm:text-lg truncate">
            {{ isEdit ? 'Edit Retailer Development Actions' : 'New Retailer Development Actions' }}
          </div>
          <div
            class="text-[10px] sm:text-xs text-(--p-anthracite-500) truncate"
            v-if="isEdit && props.value"
          >
            ID: {{ props.value.id }}
            <span v-if="props.value.createdAt">
              • Created {{ formatCreatedDate(props.value) }}
            </span>
          </div>
        </div>
      </div>
    </template>

    <Tabs v-model:value="activeTab">
      <TabList>
        <Tab :value="0"><i class="pi pi-file-edit"></i>Details</Tab>
        <Tab :value="1">
          <i class="pi pi-paperclip"></i>Attachments
          <Badge v-if="allAttachments.length > 0" :value="allAttachments.length" severity="info" />
        </Tab>
        <Tab :value="2">
          <i class="pi pi-comments"></i>Comments
          <Badge v-if="allComments.length > 0" :value="allComments.length" severity="info" />
        </Tab>
        <Tab v-if="isEdit" :value="3"><i class="pi pi-list-check"></i>Actions</Tab>
        <Tab v-if="isEdit && !isReadOnly" :value="4"><i class="pi pi-history"></i>History</Tab>
      </TabList>
      <TabPanels>
      <TabPanel :value="0">
        <form @submit.prevent="submit">
          <div class="flex flex-col gap-4 sm:gap-5">
            <div class="field mt-2 sm:mt-4">
              <label
                for="plan-title"
                class="block text-sm sm:text-base font-medium text-(--p-anthracite-500) mb-2"
              >
                <i class="pi pi-tag mr-1"></i>
                Title *
              </label>
              <InputText
                id="plan-title"
                v-model="form.title"
                class="w-full min-h-[44px]"
                :class="{ 'p-invalid': errors.title }"
                placeholder="Enter plan title..."
                :maxlength="255"
                :readonly="isReadOnly"
                :disabled="isReadOnly"
                required
                @blur="validateTitle"
                @input="validateTitle"
              />
              <small v-if="errors.title" class="p-error mt-1 block">
                <i class="pi pi-exclamation-circle mr-1"></i>
                {{ errors.title }}
              </small>
              <small v-else class="text-(--p-anthracite-500) mt-1 block">
                {{ form.title.length }}/255 characters
              </small>
            </div>

            <div class="field">
              <label
                class="block text-sm sm:text-base font-medium text-(--p-anthracite-500) mb-2 sm:mb-3"
              >
                <i class="pi pi-users mr-1"></i>
                Assignment Type *
              </label>
              <div class="flex flex-col gap-2 sm:gap-3">
                <SelectButton
                  v-model="assignmentType"
                  :options="assignmentTypeOptions"
                  option-label="label"
                  option-value="value"
                  :optionDisabled="(option) => option.value === assignmentType || isReadOnly"
                  :disabled="isReadOnly"
                  class="w-full"
                  @change="handleAssignmentTypeChange"
                >
                  <template #option="{ option }">
                    <div
                      class="flex items-center gap-1.5 sm:gap-2 px-1.5 sm:px-2 text-xs sm:text-sm"
                    >
                      <i :class="option.icon" :style="{ color: option.color }"></i>
                      <span class="truncate">{{ option.label }}</span>
                    </div>
                  </template>
                </SelectButton>
              </div>
            </div>

            <template v-if="assignmentType === 'single-retailer'">
              <div class="field">
                <label
                  for="plan-single-retailer"
                  class="block text-sm font-medium text-(--p-anthracite-500) mb-2"
                >
                  <i class="pi pi-building mr-1"></i>
                  Retailer *
                </label>
                <Select
                  id="plan-single-retailer"
                  v-model="form.singleRetailer"
                  :options="retailerSuggestions"
                  option-label="label"
                  placeholder="Select a Retailer..."
                  filter
                  :filter-fields="['label', 'code', 'bank_code']"
                  filter-placeholder="Search by name or code..."
                  class="w-full"
                  :class="{ 'p-invalid': errors.retailer }"
                  :loading="props.loadingRetailers"
                  :disabled="isReadOnly"
                  :virtualScrollerOptions="{ itemSize: 72 }"
                  @change="validateRetailer"
                >
                  <template #option="{ option }">
                    <div class="flex flex-col justify-between w-full py-1">
                      <span class="font-medium">{{ option.label || 'Unknown Retailer' }}</span>
                      <small class="text-sm text-(--p-anthracite-500)"
                        >Code: {{ option.bank_code || 'Unknown Code' }}</small
                      >
                    </div>
                  </template>
                  <template #empty>
                    <div class="p-3 text-center text-(--p-anthracite-500)">
                      <i class="pi pi-search mr-2"></i>
                      No retailers found. Try a different search term.
                    </div>
                  </template>
                </Select>
                <small v-if="errors.retailer" class="p-error mt-1 block">
                  <i class="pi pi-exclamation-circle mr-1"></i>
                  {{ errors.retailer }}
                </small>
              </div>
            </template>

            <template v-if="assignmentType === 'multiple-retailers'">
              <div class="field">
                <label
                  for="plan-multiple-retailers"
                  class="block text-sm font-medium text-(--p-anthracite-500) mb-2"
                >
                  <i class="pi pi-building mr-1"></i>
                  Retailers *
                </label>
                <MultiSelect
                  id="plan-multiple-retailers"
                  v-model="form.multipleRetailers"
                  :options="retailerSuggestions"
                  option-label="label"
                  option-value="id"
                  placeholder="Search and select Retailers..."
                  filter
                  :filter-placeholder="'Search by name or code...'"
                  :filter-fields="['label', 'code', 'bank_code']"
                  display="chip"
                  class="w-full multiselect-wrap-chips"
                  :class="{ 'p-invalid': errors.retailers }"
                  :max-selected-labels="1"
                  :selected-items-label="`{0} Retailers selected`"
                  :loading="loadingRetailers"
                  :disabled="isReadOnly"
                  :virtualScrollerOptions="{ itemSize: 72 }"
                  @blur="validateRetailers"
                  @change="validateRetailers"
                >
                  <template #option="{ option }">
                    <div class="flex flex-col justify-between w-full py-1">
                      <span class="font-medium">{{ option.label || 'Unknown Retailer' }}</span>
                      <small class="text-sm text-(--p-anthracite-500)"
                        >Code: {{ option.bank_code || 'Unknown Code' }}</small
                      >
                    </div>
                  </template>
                  <template #empty>
                    <div class="p-3 text-center text-(--p-anthracite-500)">
                      <i class="pi pi-search mr-2"></i>
                      No retailers found. Try a different search term.
                    </div>
                  </template>
                </MultiSelect>
                <small v-if="errors.retailers" class="p-error mt-1 block">
                  <i class="pi pi-exclamation-circle mr-1"></i>
                  {{ errors.retailers }}
                </small>
                <small
                  v-else-if="form.multipleRetailers.length > 0"
                  class="text-(--p-anthracite-500) mt-1 block"
                >
                  <i class="pi pi-check-circle mr-1"></i>
                  {{ form.multipleRetailers.length }} retailer{{
                    form.multipleRetailers.length !== 1 ? 's' : ''
                  }}
                  selected
                </small>
              </div>
            </template>

            <template v-if="assignmentType === 'group'">
              <div class="field">
                <label
                  for="plan-group"
                  class="block text-sm font-medium text-(--p-anthracite-500) mb-2"
                >
                  <i class="pi pi-sitemap mr-1"></i>
                  Group *
                </label>
                <Select
                  id="plan-group"
                  v-model="form.group"
                  :options="groupSuggestions"
                  option-label="label"
                  placeholder="Select a Group..."
                  filter
                  :filter-fields="['label', 'code']"
                  filter-placeholder="Search by name or code..."
                  class="w-full"
                  :class="{ 'p-invalid': errors.group }"
                  :loading="loadingGroups"
                  :disabled="isReadOnly"
                  :virtualScrollerOptions="{ itemSize: 72 }"
                  @change="handleGroupChange"
                >
                  <template #option="{ option }">
                    <div class="flex flex-col justify-between w-full py-1">
                      <span class="font-medium">{{ option.label || 'Unknown Group' }}</span>
                      <small class="text-sm text-(--p-anthracite-500)"
                        >Code: {{ option.code || 'Unknown Code' }}</small
                      >
                    </div>
                  </template>
                  <template #empty>
                    <div class="p-3 text-center text-(--p-anthracite-500)">
                      <i class="pi pi-search mr-2"></i>
                      No groups found. Try a different search term.
                    </div>
                  </template>
                </Select>
                <small v-if="errors.group" class="p-error mt-1 block">
                  <i class="pi pi-exclamation-circle mr-1"></i>
                  {{ errors.group }}
                </small>
              </div>
              <div v-if="form.group" class="field">
                <label
                  for="plan-group-retailers"
                  class="block text-sm font-medium text-(--p-anthracite-500) mb-2"
                >
                  <i class="pi pi-building mr-1"></i>
                  Select Retailers (Optional)
                </label>
                <small class="text-(--p-anthracite-500) mb-2 block">
                  Optionally select specific Retailers within this Group. If none are selected, the
                  plan will only apply to the Group.
                </small>
                <MultiSelect
                  id="plan-group-retailers"
                  v-model="form.groupRetailers"
                  :options="groupRetailerOptions"
                  option-label="label"
                  option-value="id"
                  placeholder="Search and select Retailers..."
                  filter
                  :filter-placeholder="'Search by name or code...'"
                  :filter-fields="['label', 'code', 'bank_code']"
                  display="chip"
                  class="w-full multiselect-wrap-chips"
                  :max-selected-labels="1"
                  :selected-items-label="`{0} Retailers selected`"
                  :loading="loadingRetailers"
                  :disabled="isReadOnly"
                  :virtualScrollerOptions="{ itemSize: 72 }"
                >
                  <template #option="{ option }">
                    <div class="flex flex-col justify-between w-full py-1">
                      <span class="font-medium">{{ option.label || 'Unknown Retailer' }}</span>
                      <small class="text-sm text-(--p-anthracite-500)"
                        >Code: {{ option.bank_code || 'Unknown Code' }}</small
                      >
                    </div>
                  </template>
                </MultiSelect>
                <small
                  v-if="form.groupRetailers.length > 0"
                  class="text-(--p-anthracite-500) mt-1 block"
                >
                  <i class="pi pi-check-circle mr-1"></i>
                  {{ form.groupRetailers.length }} retailer{{
                    form.groupRetailers.length !== 1 ? 's' : ''
                  }}
                  selected
                </small>
              </div>
            </template>

            <div v-if="assignmentType && !isReadOnly" class="field">
              <label class="block text-sm font-medium text-(--p-anthracite-500) mb-3">
                <i class="pi pi-user-check mr-1"></i>
                Authorised Users
              </label>
              <div
                v-if="loadingAuthorisedUsers"
                class="flex items-center justify-center p-4 sm:p-6 border border-(--p-surface-200) rounded-lg bg-(--p-surface-50) min-h-[300px] sm:min-h-[400px]"
              >
                <i class="pi pi-spin pi-spinner mr-2"></i>
                <span class="text-sm sm:text-base text-(--p-anthracite-500)"
                  >Loading authorised users...</span
                >
              </div>
              <div
                v-else-if="authorisedUsers.length === 0"
                class="flex items-center justify-center p-4 sm:p-6 border border-(--p-surface-200) rounded-lg bg-(--p-surface-50) min-h-[300px] sm:min-h-[400px]"
              >
                <i class="pi pi-info-circle mr-2 text-(--p-anthracite-500)"></i>
                <span class="text-xs sm:text-sm text-(--p-anthracite-500) text-center px-2">
                  {{
                    assignmentType === 'single-retailer' && !form.singleRetailer
                      ? 'Select a retailer to see authorised users'
                      : assignmentType === 'multiple-retailers' &&
                          form.multipleRetailers.length === 0
                        ? 'Select retailers to see authorised users'
                        : assignmentType === 'group' && !form.group
                          ? 'Select a group to see authorised users'
                          : 'No authorised users found'
                  }}
                </span>
              </div>
              <div
                v-else
                class="border border-(--p-surface-200) rounded-lg bg-(--p-surface-50) min-h-[300px] sm:min-h-[400px]"
              >
                <div class="p-2 sm:p-3 flex flex-col gap-2 sm:gap-3">
                  <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <span class="text-xs sm:text-sm text-(--p-anthracite-500) flex items-center gap-1">
                      <i class="pi pi-info-circle"></i>
                      These users will be able to view this plan:
                    </span>
                    <InputText
                      v-model="authorisedUserSearch"
                      placeholder="Search users..."
                      class="w-full sm:w-64 sm:ml-auto min-h-[44px] sm:min-h-0"
                      size="small"
                    />
                  </div>
                  <div
                    class="flex flex-col gap-2 max-h-[300px] sm:max-h-[400px] overflow-y-auto custom-scrollbar pr-1"
                  >
                    <div
                      v-for="user in filteredAuthorisedUsers"
                      :key="user.id"
                      class="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded bg-white border border-(--p-surface-200) hover:border-(--p-surface-300) transition-colors flex-shrink-0 min-h-[60px] sm:min-h-0"
                    >
                      <div
                        class="w-10 h-10 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-medium flex-shrink-0"
                        :style="{
                          backgroundColor: `hsl(${(user.id * 137.508) % 360}, 70%, 50%)`,
                        }"
                      >
                        {{
                          (user.forename?.[0] || '') + (user.surname?.[0] || '') ||
                          user.username?.[0]?.toUpperCase() ||
                          '?'
                        }}
                      </div>
                      <div class="flex-1 min-w-0">
                        <div
                          class="font-medium text-sm sm:text-base text-(--p-anthracite-500) truncate"
                        >
                          {{
                            user.forename && user.surname
                              ? `${user.forename} ${user.surname}`
                              : user.username
                          }}
                        </div>
                        <div class="text-xs sm:text-sm text-(--p-anthracite-500) truncate">
                          {{ user.email || user.username }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="isEdit" class="field">
              <div
                class="flex items-center justify-between p-3 border border-(--p-surface-200) rounded-lg"
              >
                <div class="flex items-center gap-3">
                  <i
                    class="pi text-lg"
                    :class="
                      form.active
                        ? 'pi-check-circle text-(--p-mint-500)'
                        : 'pi-times-circle text-(--p-anthracite-500)'
                    "
                  ></i>
                  <div>
                    <label
                      for="plan-active"
                      class="block text-sm font-medium text-(--p-anthracite-500)"
                    >
                      Complete
                    </label>
                    <small class="text-(--p-anthracite-500)">
                      {{ form.active ? 'Plan is marked as complete' : 'Plan is in progress' }}
                    </small>
                  </div>
                </div>
                <ToggleSwitch id="plan-active" v-model="form.active" :disabled="isReadOnly" />
              </div>
            </div>
          </div>
        </form>
      </TabPanel>

      <TabPanel :value="1">
        <div class="flex flex-col gap-4">
          <div class="field">
            <FileUpload
              name="files[]"
              :multiple="true"
              :customUpload="true"
              :auto="true"
              :disabled="uploading || isReadOnly"
              chooseLabel="Choose Files"
              uploadLabel="Upload"
              cancelLabel="Clear"
              @uploader="onFilesUpload"
            >
              <template #empty>
                <div class="flex flex-col items-center justify-center py-8">
                  <i class="pi pi-cloud-upload !text-4xl text-(--p-anthracite-500) mb-3"></i>
                  <p class="text-sm text-(--p-anthracite-500)">Drag and drop files here to upload</p>
                </div>
              </template>
            </FileUpload>
          </div>

          <div v-if="allAttachments.length > 0" class="field">
            <label class="block text-sm font-medium text-(--p-anthracite-500) mb-2">
              {{ isEdit ? 'Uploaded Files' : 'Pending Files' }}
            </label>
            <div class="flex flex-col gap-2">
              <div
                v-for="(att, index) in allAttachments"
                :key="att.id || `pending-${index}`"
                class="flex items-center justify-between p-3 border border-(--p-surface-200) rounded-lg hover:border-(--p-primary-300) transition-colors"
              >
                <div class="flex items-center gap-3 flex-1 min-w-0">
                  <i class="pi pi-file !text-xl text-(--p-primary-500)"></i>
                  <div class="flex-1 min-w-0">
                    <div class="font-medium text-sm truncate">
                      {{ att.originalName || (att as { name?: string }).name }}
                    </div>
                    <div class="text-xs text-(--p-anthracite-500)">
                      {{ formatFileSize(att.sizeBytes || (att as { size?: number }).size || 0) }}
                      <span v-if="!isEdit && !att.id" class="text-yellow-600 ml-2"
                        >(Pending upload)</span
                      >
                    </div>
                  </div>
                </div>
                <div class="flex items-center gap-1">
                  <Button
                    v-if="isEdit && att.id"
                    icon="pi pi-download"
                    severity="secondary"
                    text
                    rounded
                    size="small"
                    @click="downloadAttachment(att)"
                    v-tooltip.top="'Download file'"
                  />
                  <Button
                    icon="pi pi-trash"
                    severity="danger"
                    text
                    rounded
                    size="small"
                    @click="removeAttachment(att, index)"
                    v-tooltip.top="'Remove file'"
                  />
                </div>
              </div>
            </div>
          </div>

          <Message v-else severity="info" :closable="false">
            No attachments yet. Upload files to attach them to this plan.
          </Message>
        </div>
      </TabPanel>

      <TabPanel :value="2">
        <div class="flex flex-col gap-4">
          <div class="field" v-if="!isReadOnly">
            <label class="block text-sm font-medium text-(--p-anthracite-500) mb-2">
              Add a comment
            </label>
            <div class="flex gap-2">
              <InputText
                v-model="newComment"
                placeholder="Write a comment..."
                class="flex-1 min-h-[44px]"
                @keyup.enter="postNewComment"
              />
              <Button
                icon="pi pi-send"
                label="Post"
                @click="postNewComment"
                :disabled="!newComment.trim()"
                class="min-h-[44px] sm:min-h-0"
              />
            </div>
          </div>

          <div v-if="allComments.length > 0" class="field">
            <label class="block text-sm font-medium text-(--p-anthracite-500) mb-2">
              Discussion
            </label>
            <div class="flex flex-col gap-3 max-h-96 overflow-y-auto custom-scrollbar pr-2">
              <div
                v-for="(c, index) in allComments"
                :key="c.id || `pending-${index}`"
                class="p-3 rounded-lg border border-(--p-surface-200) bg-(--p-surface-50) hover:border-(--p-primary-300) transition-colors"
              >
                <div class="flex items-start gap-3 mb-2">
                  <div
                    class="w-8 h-8 rounded-full bg-(--p-primary-500) flex items-center justify-center text-white text-sm font-semibold"
                  >
                    <i class="pi pi-user"></i>
                  </div>
                  <div class="flex-1">
                    <div class="flex items-center gap-2 mb-1">
                      <span class="font-medium text-sm text-(--p-anthracite-500)">
                        {{ getCommentAuthor(c).fullName }}
                        <small
                          v-if="getCommentAuthor(c).username"
                          class="text-(--p-anthracite-500) font-normal"
                        >
                          ({{ getCommentAuthor(c).username }})
                        </small>
                      </span>
                      <span class="text-xs text-(--p-anthracite-500)">
                        {{ c.createdAt ? formatRelativeTime(c.createdAt) : '' }}
                      </span>
                    </div>
                    <div class="text-sm text-(--p-anthracite-500) whitespace-pre-line">
                      {{ c.body }}
                    </div>
                  </div>
                </div>

                <div
                  v-if="c.replies?.length"
                  class="ml-11 mt-3 pl-3 border-l-2 border-(--p-surface-300) space-y-2"
                >
                  <div v-for="r in c.replies" :key="r.id" class="flex items-start gap-2">
                    <div
                      class="w-6 h-6 rounded-full bg-(--p-surface-300) flex items-center justify-center text-xs"
                    >
                      <i class="pi pi-user text-[10px]"></i>
                    </div>
                    <div class="flex-1">
                      <div class="flex items-center gap-2 mb-1">
                        <span class="font-medium text-xs text-(--p-anthracite-500)">
                          {{ getCommentAuthor(r).fullName }}
                          <small
                            v-if="getCommentAuthor(r).username"
                            class="text-(--p-anthracite-500) font-normal"
                          >
                            ({{ getCommentAuthor(r).username }})
                          </small>
                        </span>
                        <span class="text-[10px] text-(--p-anthracite-500)">
                          {{ r.createdAt ? formatRelativeTime(r.createdAt) : '' }}
                        </span>
                      </div>
                      <div class="text-xs text-(--p-anthracite-500)">{{ r.body }}</div>
                    </div>
                  </div>
                </div>

                <div v-if="c.id && !isReadOnly" class="mt-3 ml-11 flex gap-2">
                  <InputText
                    v-model="replyBuffer[c.id]"
                    placeholder="Write a reply..."
                    size="small"
                    class="flex-1"
                    @keyup.enter="postReply(c.id)"
                  />
                  <Button
                    icon="pi pi-reply"
                    label="Reply"
                    size="small"
                    text
                    @click="postReply(c.id)"
                    :disabled="!replyBuffer[c.id]?.trim()"
                  />
                </div>
              </div>
            </div>
          </div>

          <Message v-else severity="info" :closable="false">
            No comments yet. Be the first to comment!
          </Message>
        </div>
      </TabPanel>

      <TabPanel v-if="isEdit" :value="3">
        <div class="actions-tab-content">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold text-(--p-anthracite-500) m-0">Plan Actions</h3>
            <Button
              v-if="!isReadOnly"
              icon="pi pi-plus"
              label="Add Action"
              size="small"
              @click="handleCreateAction"
              v-tooltip.bottom="'Add a new action to this plan'"
            />
          </div>
          <div
            v-if="!props.value?.actions || props.value.actions.length === 0"
            class="empty-actions flex flex-col items-center justify-center py-12"
          >
            <i class="pi pi-inbox !text-4xl text-(--p-anthracite-500) mb-3"></i>
            <p class="text-(--p-anthracite-500) mb-2">No actions in this plan</p>
            <p class="text-sm text-(--p-anthracite-500) mb-4">
              Actions will appear here once added to the plan
            </p>
            <Button
              v-if="!isReadOnly"
              icon="pi pi-plus"
              label="Add Your First Action"
              @click="handleCreateAction"
            />
          </div>
          <div v-else class="actions-list space-y-2">
            <div
              v-for="action in props.value.actions"
              :key="action.id"
              class="action-item p-3 border border-(--p-surface-200) rounded-lg hover:border-(--p-primary-400) hover:bg-(--p-surface-50) transition-all cursor-pointer group"
              @click="handleActionClick(action)"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="flex items-start gap-3 flex-1 min-w-0">
                  <div
                    class="w-1 h-12 rounded-full flex-shrink-0 mt-0.5"
                    :style="{ backgroundColor: action.colorHex || '#60a5fa' }"
                  ></div>
                  <div class="flex-1 min-w-0">
                    <div
                      class="font-semibold text-(--p-anthracite-500) group-hover:text-(--p-primary-700) transition-colors mb-1 truncate"
                    >
                      {{ action.title }}
                    </div>
                    <div
                      v-if="action.details"
                      class="text-sm text-(--p-anthracite-500) line-clamp-2 mb-2"
                    >
                      {{ action.details }}
                    </div>
                    <div class="flex flex-wrap gap-2 items-center">
                      <span
                        class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                        :class="getStateBadgeClass(action.state)"
                      >
                        <i :class="getStateIcon(action.state)" class="text-[10px]"></i>
                        <span>{{ getStateLabel(action.state) }}</span>
                      </span>
                      <div
                        class="inline-flex items-center gap-1 px-2 py-1 rounded text-xs"
                        :class="
                          isOverdue(action)
                            ? 'bg-red-100 text-red-700 font-medium'
                            : 'bg-(--p-surface-100) text-(--p-anthracite-500)'
                        "
                      >
                        <i
                          :class="
                            isOverdue(action) ? 'pi pi-exclamation-triangle' : 'pi pi-calendar'
                          "
                          class="text-[10px]"
                        ></i>
                        <span>{{ formatActionDate(action.dueDate) }}</span>
                      </div>
                      <div
                        v-if="action.kpiArea"
                        class="inline-flex items-center gap-1 px-2 py-1 rounded text-xs"
                        :class="getKpiAreaColorClass()"
                      >
                        <i class="pi pi-chart-line text-[10px]"></i>
                        <span>{{ action.kpiArea }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <i
                  class="pi pi-chevron-right text-(--p-anthracite-500) group-hover:text-(--p-primary-600) transition-colors flex-shrink-0"
                ></i>
              </div>
            </div>
          </div>
        </div>
      </TabPanel>

      <TabPanel v-if="isEdit && !isReadOnly" :value="4">
        <div class="py-4">
          <div v-if="historyLoading" class="flex items-center justify-center py-8">
            <ProgressSpinner />
          </div>
          <div v-else-if="historyError" class="p-4">
            <Message severity="error" :closable="false">
              {{ historyError }}
            </Message>
          </div>
          <div
            v-else-if="planHistory.length === 0"
            class="flex flex-col items-center justify-center py-12 px-4 text-center"
          >
            <i class="pi pi-inbox !text-4xl text-(--p-anthracite-500) mb-3"></i>
            <p class="text-(--p-anthracite-500)">No history available for this plan</p>
          </div>
          <div v-else class="relative pl-8">
            <div
              v-for="(log, index) in planHistory"
              :key="log.id"
              class="relative pb-8 pl-8"
              :class="{
                'timeline-item-last': index === planHistory.length - 1,
              }"
            >
              <div
                v-if="index !== planHistory.length - 1"
                class="absolute left-2 top-10 -bottom-2 w-0.5 bg-(--p-surface-200)"
              ></div>
              <div
                class="absolute -left-6 top-1 w-8 h-8 rounded-full flex items-center justify-center border-2 border-white shadow-sm z-10"
                :class="{
                  'bg-(--p-mint-500) text-white': log.event === 'created',
                  'bg-(--p-primary-400) text-white': log.event === 'updated',
                  'bg-red-500 text-white': log.event === 'deleted',
                  'bg-amber-500 text-white': log.event === 'restored',
                }"
              >
                <i
                  :class="[
                    getEventIcon(log.event),
                    ['created', 'updated', 'deleted', 'restored'].includes(log.event) ? 'text-white' : '',
                  ]"
                ></i>
              </div>
              <div
                class="bg-(--p-surface-50) border border-(--p-surface-200) rounded-lg p-4 transition-all hover:shadow-md hover:border-(--p-primary-300)"
              >
                <div
                  class="flex justify-between items-center mb-3 flex-wrap gap-2 sm:flex-col sm:items-start"
                >
                  <div class="flex items-center gap-2">
                    <Badge
                      :value="getEventLabel(log.event, log.auditable_type)"
                      :severity="getEventSeverity(log.event)"
                      class="capitalize"
                    />
                    <span class="text-xs text-(--p-anthracite-500) font-medium">{{
                      formatHistoryDate(log.created_at)
                    }}</span>
                  </div>
                  <div class="flex items-center gap-1 text-xs text-(--p-anthracite-500)">
                    <i class="pi pi-user"></i>
                    <span>{{ getUserDisplayName(log.user) }}</span>
                  </div>
                </div>
                <div class="timeline-body">
                  <div
                    v-if="getEventDescription(log, 'plan')"
                    class="text-sm text-(--p-anthracite-500) mb-3"
                  >
                    {{ getEventDescription(log, 'plan') }}
                  </div>
                  <div
                    v-if="log.changed_attributes && log.changed_attributes.length > 0"
                    class="mt-2 space-y-2"
                  >
                    <div
                      v-for="attr in log.changed_attributes"
                      :key="attr"
                      class="change-item p-3 bg-(--p-surface-50) rounded-lg border border-(--p-surface-200)"
                    >
                      <div class="flex items-center gap-2 mb-2">
                        <i class="pi pi-arrow-right text-xs text-(--p-primary-500)"></i>
                        <span class="text-sm font-semibold text-(--p-anthracite-500)">
                          {{ getFieldLabel(attr) }}
                        </span>
                      </div>
                      <div
                        v-if="log.old_values && log.old_values[attr] !== undefined"
                        class="flex items-start gap-3"
                      >
                        <div class="flex-1">
                          <div class="text-xs text-(--p-anthracite-500) mb-1">Previous</div>
                          <div class="text-sm text-(--p-anthracite-500) line-through text-red-600">
                            {{ formatFieldValue(attr, log.old_values[attr]) }}
                          </div>
                        </div>
                        <i class="pi pi-arrow-right text-xs text-(--p-primary-500) mt-4"></i>
                        <div class="flex-1">
                          <div class="text-xs text-(--p-anthracite-500) mb-1">Current</div>
                          <div class="text-sm font-medium text-(--p-anthracite-500)">
                            {{ formatFieldValue(attr, log.new_values?.[attr]) }}
                          </div>
                        </div>
                      </div>
                      <div v-else class="text-sm text-(--p-anthracite-500)">
                        {{ formatFieldValue(attr, log.new_values?.[attr]) }}
                      </div>
                    </div>
                  </div>
                  <div
                    v-else-if="log.new_values && Object.keys(log.new_values).length > 0"
                     class="mt-2 p-3 bg-(--p-mint-300) rounded-lg border border-(--p-mint-500)"
                   >
                    <div class="text-sm font-semibold text-(--p-mint-700) mb-2">
                      <i class="pi pi-check-circle mr-1"></i>
                      {{ getEventLabel(log.event, log.auditable_type) }}
                    </div>
                    <div class="space-y-1">
                      <div
                        v-for="(value, key) in log.new_values"
                        :key="key"
                        class="text-sm text-(--p-anthracite-500)"
                      >
                        <span class="font-medium">{{ getFieldLabel(key) }}:</span>
                        <span class="ml-2">{{ formatFieldValue(key, value) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </TabPanel>
      </TabPanels>
    </Tabs>

    <template #footer>
      <div class="flex items-stretch sm:items-center justify-between w-full gap-3 sm:gap-0">
        <div v-if="!isReadOnly && isEdit" class="flex gap-2 justify-center sm:justify-start">
          <Button
            type="button"
            :label="isMobile ? undefined : 'Duplicate'"
            icon="pi pi-copy"
            severity="secondary"
            text
            :loading="duplicating || deleting"
            :disabled="deleting"
            @click="duplicate"
            class="min-h-[44px] min-w-[44px] rounded-lg sm:min-h-0 sm:min-w-0"
            v-tooltip.bottom="'Duplicate'"
          />
          <Button
            type="button"
            :label="isMobile ? undefined : 'Delete'"
            icon="pi pi-trash"
            severity="danger"
            outlined
            :loading="deleting"
            :disabled="duplicating"
            @click="confirmDelete"
            class="min-h-[44px] min-w-[44px] rounded-lg sm:min-h-0 sm:min-w-0"
            v-tooltip.bottom="'Delete'"
          />
        </div>

        <div class="flex flex-1 gap-2 justify-end w-full sm:w-auto">
          <Button
            v-if="!isReadOnly && !isMobile"
            type="button"
            label="Cancel"
            icon="pi pi-times"
            severity="secondary"
            outlined
            :disabled="duplicating || deleting"
            @click="cancel"
            class="flex-1 sm:flex-none min-h-[48px] sm:min-h-0 rounded-lg font-medium"
          />
          <Button
            v-if="!isReadOnly"
            type="submit"
            :label="!isMobile ? (isEdit ? 'Save Changes' : 'Create Plan') : undefined"
            icon="pi pi-check"
            severity="primary"
            :loading="saving"
            :disabled="duplicating || deleting"
            :class="{ 'opacity-50 cursor-not-allowed': !isFormValid && !saving }"
            class="flex-1 sm:flex-none min-h-[48px] sm:min-h-0 rounded-lg font-semibold"
            @click="
              (e) => {
                if (!isFormValid && !saving && !duplicating && !deleting) {
                  e.preventDefault()
                  showValidationErrors()
                } else {
                  submit()
                }
              }
            "
          />
          <Button
            v-if="isReadOnly"
            type="button"
            label="Close"
            icon="pi pi-times"
            severity="secondary"
            outlined
            @click="cancel"
            class="flex-1 sm:flex-none min-h-[48px] sm:min-h-0 rounded-lg font-medium"
          />
        </div>
      </div>
    </template>
  </Dialog>

  <Dialog
    v-model:visible="showDeleteConfirm"
    modal
    header="Delete Plan"
    :style="{ width: '90vw', maxWidth: '500px' }"
  >
    <template #header>
      <div class="flex items-center gap-3">
        <i class="pi pi-exclamation-triangle text-red-600 text-2xl"></i>
        <span class="font-bold text-lg">Delete Plan</span>
      </div>
    </template>
    <div class="p-4">
      <p class="mb-4 text-(--p-anthracite-500)">
        Are you sure you want to delete the plan
        <strong>"{{ props.value?.title }}"</strong>?
      </p>
      <p class="text-sm text-(--p-anthracite-500)">
        This action will soft delete the plan. It will be hidden from view but can be restored if
        needed.
      </p>
    </div>
    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          label="Cancel"
          icon="pi pi-times"
          severity="secondary"
          outlined
          :disabled="deleting"
          @click="showDeleteConfirm = false"
        />
        <Button
          label="Delete"
          icon="pi pi-trash"
          severity="danger"
          :loading="deleting"
          @click="handleDelete"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { reactive, computed, watch, ref, onMounted, onUnmounted } from 'vue'
import axios from 'axios'
import type { Plan, PlanAttachment, PlanComment } from '@/config/types/Plan'
import type { Retailer } from '@/config/types/Retailer'
import type { Group } from '@/config/types/Group'
import type { Action } from '@/config/types/Action'
import usePlans from '@/composables/Plans'
import useNotifications from '@/composables/Notification'
import useAuditLogs, { type AuditLog } from '@/composables/AuditLogs'
import useAuth from '@/composables/Auth'
import { isActionOverdue } from '@/composables/ActionFilters'
import useActions from '@/composables/Actions'
import {
  getEventIcon,
  getEventSeverity,
  getUserDisplayName,
  formatHistoryDate,
  getFieldLabel,
  formatFieldValue,
  getEventLabel,
  getEventDescription,
} from '@/composables/useAuditLogFormatter'
import { formatRelativeTime } from '@/utils/formatters'
import type { AssignmentType } from '@/config/types/PlanEditor'
import type { SelectOption } from '@/config/types/Common'

const props = defineProps<{
  modelValue: boolean
  value?: Plan | null
  availableRetailers?: Retailer[]
  retailerSuggestions?: SelectOption[]
  groupSuggestions?: SelectOption[]
  loadingRetailers?: boolean
  loadingGroups?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'save', value: Plan): void
  (e: 'cancel'): void
  (e: 'duplicated', value: Plan): void
  (e: 'editDuplicated', value: Plan): void
  (e: 'deleted', planId: string | number): void
  (e: 'action:click', value: Action): void
  (e: 'action:create', planId: string | number): void
  (e: 'save:success'): void
  (e: 'save:error', error: unknown): void
}>()

const visible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => {
    emit('update:modelValue', value)
    if (!value) {
      activeTab.value = 0
      planHistory.value = []
      historyError.value = null
      saving.value = false
      uploading.value = false
      form.id = undefined
      form.title = ''
      form.active = false
      form.singleRetailer = null
      form.multipleRetailers = []
      form.group = null
      form.groupRetailers = []
      assignmentType.value = 'single-retailer'
      authorisedUsers.value = []
      selectedRetailersCache.value.clear()
      selectedGroupsCache.value.clear()
      localAttachments.value = []
      pendingAttachments.value = []
      comments.value = []
      pendingComments.value = []
      newComment.value = ''
      replyBuffer.value = {}
      Object.keys(errors).forEach((key) => delete errors[key as keyof typeof errors])
      emit('cancel')
    }
  },
})

const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024)
const isMobile = computed(() => windowWidth.value < 640)

const handleResize = () => {
  if (typeof window !== 'undefined') {
    windowWidth.value = window.innerWidth
  }
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', handleResize)
  }
})

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', handleResize)
  }
})

const isEdit = computed(() => Boolean(props.value && props.value.id))
const loadingRetailers = computed(() => props.loadingRetailers ?? false)
const loadingGroups = computed(() => props.loadingGroups ?? false)

const { user } = useAuth()
const isReadOnly = computed(() => {
  const userAccess = (user.value as { access?: number })?.access
  return userAccess !== undefined && userAccess !== null && userAccess < 5
})
const getCommentAuthor = (comment: PlanComment) => {
  const source = comment.user || (user.value as { forename?: string; surname?: string; username?: string })
  const fullName = [source?.forename, source?.surname].filter(Boolean).join(' ').trim()
  const username = source?.username || ''

  return {
    fullName: fullName || username || 'User',
    username,
  }
}

const assignmentTypeOptions = [
  {
    label: 'Single Retailer',
    value: 'single-retailer',
    icon: 'pi pi-building',
    color: '#334eb8',
  },
  {
    label: 'Multiple Retailers',
    value: 'multiple-retailers',
    icon: 'pi pi-building',
    color: '#334eb8',
  },
  {
    label: 'Group',
    value: 'group',
    icon: 'pi pi-sitemap',
    color: '#334eb8',
  },
]

const assignmentType = ref<AssignmentType>('single-retailer')

const form = reactive<{
  id?: string | number
  title: string
  active: boolean
  singleRetailer: SelectOption | null
  multipleRetailers: Array<string | number>
  group: SelectOption | null
  groupRetailers: Array<string | number>
}>({
  id: undefined,
  title: '',
  active: false,
  singleRetailer: null,
  multipleRetailers: [],
  group: null,
  groupRetailers: [],
})

const errors = reactive<{
  title?: string
  retailer?: string
  retailers?: string
  group?: string
}>({})

const saving = ref(false)
const uploading = ref(false)
const duplicating = ref(false)
const deleting = ref(false)
const showDeleteConfirm = ref(false)
const loadingAuthorisedUsers = ref(false)
const authorisedUsers = ref<
  Array<{
    id: number
    username: string
    forename: string | null
    surname: string | null
    email: string | null
  }>
>([])
const authorisedUserSearch = ref('')
const filteredAuthorisedUsers = computed(() => {
  const term = authorisedUserSearch.value.trim().toLowerCase()
  if (!term) return authorisedUsers.value
  return authorisedUsers.value.filter((user) => {
    const fullName = `${user.forename ?? ''} ${user.surname ?? ''}`.toLowerCase()
    return (
      (user.username && user.username.toLowerCase().includes(term)) ||
      (user.email && user.email.toLowerCase().includes(term)) ||
      fullName.trim().includes(term)
    )
  })
})
const activeTab = ref(0)
const planHistory = ref<AuditLog[]>([])
const historyLoading = ref(false)
const historyError = ref<string | null>(null)
const localAttachments = ref<PlanAttachment[]>([])
const pendingAttachments = ref<File[]>([])
const comments = ref<PlanComment[]>([])
const pendingComments = ref<Array<{ body: string; parentId?: string | number | null }>>([])
const newComment = ref('')
const replyBuffer = ref<Record<string | number, string>>({})

const selectedRetailersCache = ref<Map<string | number, SelectOption>>(new Map())
const selectedGroupsCache = ref<Map<string | number, SelectOption>>(new Map())
const {
  duplicatePlan,
  deletePlan,
  createPlan,
  updatePlan,
  uploadPlanAttachment,
  downloadPlanAttachment,
  deletePlanAttachment,
  fetchPlanAttachments,
  fetchPlanComments,
  postPlanComment,
} = usePlans()
const { toast } = useNotifications()
const { fetchAuditLogs, logAuditEvent } = useAuditLogs()
const lastViewedPlanId = ref<number | string | null>(null)

watch(activeTab, (newIndex) => {
  if (isEdit.value && props.value?.id && newIndex === 4) {
    loadHistory()
  }
})

watch(
  () => props.value,
  async (newValue) => {
    activeTab.value = 0
    planHistory.value = []
    historyError.value = null
    uploading.value = false
    localAttachments.value = []
    pendingAttachments.value = []
    comments.value = []
    pendingComments.value = []
    newComment.value = ''
    replyBuffer.value = {}

    if (newValue) {
      form.id = newValue.id
      form.title = newValue.title
      form.active = newValue.active

      if (newValue.groups?.length) {
        assignmentType.value = 'group'
        const group = newValue.groups[0]
        form.group = {
          id: group.id,
          label: group.label,
          code: group.code,
        }
        selectedGroupsCache.value.set(group.id, form.group)
        form.singleRetailer = null
        form.multipleRetailers = []

        // If plan has both groups and retailers, populate groupRetailers
        if (newValue.retailers?.length > 0) {
          form.groupRetailers = newValue.retailers.map((r) => r.id)
          const retailerOptions = newValue.retailers.map((r) => ({
            id: r.id,
            label: r.label,
            code: r.code,
            bank_code: r.bank_code,
            group_id: group.id,
          }))
          retailerOptions.forEach((r) => {
            selectedRetailersCache.value.set(r.id, r)
          })
        } else {
          form.groupRetailers = []
        }
      } else if (newValue.retailers?.length > 1) {
        assignmentType.value = 'multiple-retailers'
        const retailerOptions = newValue.retailers.map((r) => ({
          id: r.id,
          label: r.label,
          code: r.code,
          bank_code: r.bank_code,
        }))
        retailerOptions.forEach((r) => {
          selectedRetailersCache.value.set(r.id, r)
        })
        form.multipleRetailers = newValue.retailers.map((r) => r.id)
        form.singleRetailer = null
        form.group = null
      } else if (newValue.retailers?.length === 1) {
        assignmentType.value = 'single-retailer'
        const retailer = newValue.retailers[0]
        form.singleRetailer = {
          id: retailer.id,
          label: retailer.label,
          code: retailer.code,
          bank_code: retailer.bank_code,
        }
        selectedRetailersCache.value.set(retailer.id, form.singleRetailer)
        form.multipleRetailers = []
        form.group = null
      } else {
        assignmentType.value = 'single-retailer'
        form.singleRetailer = null
        form.multipleRetailers = []
        form.group = null
      }

      if (newValue.id) {
        try {
          const [loadedComments, loadedAttachments] = await Promise.all([
            fetchPlanComments(newValue.id),
            fetchPlanAttachments(newValue.id),
          ])
          comments.value = loadedComments
          localAttachments.value = loadedAttachments
        } catch (error) {
          console.warn('Failed to load plan discussion data:', error)
          comments.value = newValue.comments || []
          localAttachments.value = newValue.attachments || []
        }
      } else {
        comments.value = newValue.comments || []
        localAttachments.value = newValue.attachments || []
      }
    } else {
      form.id = undefined
      form.title = ''
      form.active = false
      form.singleRetailer = null
      form.multipleRetailers = []
      form.group = null
      form.groupRetailers = []
      assignmentType.value = 'single-retailer'
      selectedRetailersCache.value.clear()
      selectedGroupsCache.value.clear()
      localAttachments.value = []
      pendingAttachments.value = []
      comments.value = []
      pendingComments.value = []
      newComment.value = ''
      replyBuffer.value = {}
    }
    Object.keys(errors).forEach((key) => delete errors[key as keyof typeof errors])
  },
  { immediate: true },
)

watch(
  () => [props.modelValue, props.value?.id],
  async ([isOpen, planId]) => {
    if (!isOpen || !planId) {
      lastViewedPlanId.value = null
      return
    }

    const normalizedId = Number(planId)
    if (Number.isNaN(normalizedId)) {
      return
    }

    if (lastViewedPlanId.value === normalizedId) {
      return
    }

    lastViewedPlanId.value = normalizedId

    try {
      await logAuditEvent({
        auditableType: 'Plan',
        auditableId: normalizedId,
        event: 'viewed',
      })
    } catch (error) {
      console.warn('Failed to log plan view:', error)
    }
  },
)

const retailerSuggestions = computed(() => {
  const propsList = props.retailerSuggestions || []
  const cachedList = Array.from(selectedRetailersCache.value.values())

  const combined = new Map<string | number, SelectOption>()
  propsList.forEach((r) => combined.set(r.id, r))
  cachedList.forEach((r) => combined.set(r.id, r))

  return Array.from(combined.values())
})

const groupRetailerOptions = computed(() => {
  if (!form.group?.id) {
    return []
  }

  const groupId = Number(form.group.id)
  const filtered = retailerSuggestions.value.filter((retailer) => {
    const retailerWithGroupId = retailer as SelectOption & { group_id?: string | number | null }
    const retailerGroupId = retailerWithGroupId?.group_id

    if (retailerGroupId === null || retailerGroupId === undefined) {
      return false
    }

    const retailerGroupIdNum = Number(retailerGroupId)
    const groupIdNum = Number(groupId)

    return retailerGroupIdNum === groupIdNum
  })

  return filtered
})

const groupSuggestions = computed(() => {
  const propsList = props.groupSuggestions || []
  const cachedList = Array.from(selectedGroupsCache.value.values())

  const combined = new Map<string | number, SelectOption>()
  propsList.forEach((g) => combined.set(g.id, g))
  cachedList.forEach((g) => combined.set(g.id, g))

  return Array.from(combined.values())
})

let fetchAuthorisedUsersTimeout: ReturnType<typeof setTimeout> | null = null

async function fetchAuthorisedUsers() {
  if (!assignmentType.value) {
    authorisedUsers.value = []
    return
  }

  const retailerIds: (string | number)[] = []
  const groupIds: (string | number)[] = []

  if (assignmentType.value === 'single-retailer' && form.singleRetailer) {
    retailerIds.push(form.singleRetailer.id)
  } else if (assignmentType.value === 'multiple-retailers' && form.multipleRetailers.length > 0) {
    retailerIds.push(...form.multipleRetailers)
  } else if (assignmentType.value === 'group' && form.group) {
    groupIds.push(form.group.id)
    if (form.groupRetailers.length > 0) {
      retailerIds.push(...form.groupRetailers)
    }
  }

  if (retailerIds.length === 0 && groupIds.length === 0) {
    authorisedUsers.value = []
    return
  }

  if (fetchAuthorisedUsersTimeout) {
    clearTimeout(fetchAuthorisedUsersTimeout)
  }

  fetchAuthorisedUsersTimeout = setTimeout(async () => {
    loadingAuthorisedUsers.value = true
    try {
      const params = new URLSearchParams()
      if (retailerIds.length > 0) {
        retailerIds.forEach((id) => params.append('retailer_ids[]', String(id)))
      }
      if (groupIds.length > 0) {
        groupIds.forEach((id) => params.append('group_ids[]', String(id)))
      }

      const response = await axios.get(`/api/users/authorised?${params.toString()}`)
      authorisedUsers.value = response.data
    } catch (error) {
      console.error('Failed to fetch authorised users:', error)
      authorisedUsers.value = []
    } finally {
      loadingAuthorisedUsers.value = false
      fetchAuthorisedUsersTimeout = null
    }
  }, 100)
}

watch(assignmentType, (newType) => {
  if (
    (newType === 'single-retailer' && form.singleRetailer?.id) ||
    (newType === 'multiple-retailers' &&
      form.multipleRetailers &&
      form.multipleRetailers.length > 0) ||
    (newType === 'group' && form.group?.id)
  ) {
    fetchAuthorisedUsers()
  }
})

watch(
  () => form.singleRetailer,
  () => {
    if (assignmentType.value === 'single-retailer' && form.singleRetailer?.id) {
      fetchAuthorisedUsers()
    }
  },
)

watch(
  () => form.multipleRetailers,
  () => {
    if (
      assignmentType.value === 'multiple-retailers' &&
      form.multipleRetailers &&
      form.multipleRetailers.length > 0
    ) {
      fetchAuthorisedUsers()
    }
  },
  { deep: true },
)

watch(
  () => form.group,
  () => {
    if (assignmentType.value === 'group' && form.group?.id) {
      fetchAuthorisedUsers()
    }
  },
)

watch(
  () => form.groupRetailers,
  () => {
    if (assignmentType.value === 'group' && form.group?.id) {
      fetchAuthorisedUsers()
    }
  },
  { deep: true },
)

function validateTitle() {
  if (!form.title.trim()) {
    errors.title = 'Title is required'
  } else if (form.title.length > 255) {
    errors.title = 'Title must be 255 characters or less'
  } else {
    delete errors.title
  }
}

function validateRetailer() {
  if (
    assignmentType.value === 'single-retailer' &&
    !(form.singleRetailer?.code || form.singleRetailer?.bank_code)
  ) {
    errors.retailer = 'Please select a Retailer'
    return
  } else {
    delete errors.retailer
  }
}

function validateRetailers() {
  if (assignmentType.value === 'multiple-retailers' && form.multipleRetailers.length === 0) {
    errors.retailers = 'Please select at least one Retailer'
    return
  } else {
    delete errors.retailers
  }
}

function validateGroup() {
  if (assignmentType.value === 'group' && !form.group?.code) {
    errors.group = 'Please select a Group'
    return
  } else {
    delete errors.group
  }
}

function handleGroupChange() {
  validateGroup()
  form.groupRetailers = []
  if (form.group?.id) {
    fetchAuthorisedUsers()
  }
}

function handleAssignmentTypeChange() {
  form.singleRetailer = null
  form.multipleRetailers = []
  form.group = null
  form.groupRetailers = []

  delete errors.retailer
  delete errors.retailers
  delete errors.group

  validateRetailer()
  validateRetailers()
  validateGroup()

  authorisedUsers.value = []
}

function validateForm(): boolean {
  validateTitle()
  if (assignmentType.value === 'single-retailer') {
    validateRetailer()
  } else if (assignmentType.value === 'multiple-retailers') {
    validateRetailers()
  } else if (assignmentType.value === 'group') {
    validateGroup()
  }
  return Object.keys(errors).length === 0
}

function showValidationErrors() {
  const errorMessages: string[] = []

  if (errors.title) {
    errorMessages.push(errors.title)
  }
  if (errors.retailer) {
    errorMessages.push(errors.retailer)
  }
  if (errors.retailers) {
    errorMessages.push(errors.retailers)
  }
  if (errors.group) {
    errorMessages.push(errors.group)
  }

  if (errorMessages.length > 0) {
    const message =
      errorMessages.length === 1
        ? errorMessages[0]
        : `Please fix the following errors:\n• ${errorMessages.join('\n• ')}`
    toast('Validation Error', message, 'error')
  } else {
    if (!form.title.trim()) {
      toast('Validation Error', 'Title is required', 'error')
    } else if (form.title.length > 255) {
      toast('Validation Error', 'Title must be 255 characters or less', 'error')
    } else if (assignmentType.value === 'single-retailer' && !form.singleRetailer) {
      toast('Validation Error', 'Please select a Retailer', 'error')
    } else if (
      assignmentType.value === 'multiple-retailers' &&
      form.multipleRetailers.length === 0
    ) {
      toast('Validation Error', 'Please select at least one Retailer', 'error')
    } else if (assignmentType.value === 'group' && !form.group) {
      toast('Validation Error', 'Please select a Group', 'error')
    }
  }
}

const isFormValid = computed(() => {
  if (!form.title.trim()) {
    return false
  }
  if (form.title.length > 255) {
    return false
  }
  if (assignmentType.value === 'single-retailer' && !form.singleRetailer) {
    return false
  }
  if (assignmentType.value === 'multiple-retailers' && form.multipleRetailers.length === 0) {
    return false
  }
  if (assignmentType.value === 'group' && !form.group) {
    return false
  }
  return true
})

const formatCreatedDate = (plan: Plan) => {
  if (!plan.createdAt) return ''
  return formatRelativeTime(plan.createdAt)
}

type PendingAttachment = {
  id?: undefined
  originalName: string
  sizeBytes: number
  name: string
  size: number
}

const allAttachments = computed(() => {
  const uploaded = localAttachments.value || []
  const pending = pendingAttachments.value.map(
    (file): PendingAttachment => ({
      id: undefined,
      originalName: file.name,
      sizeBytes: file.size,
      name: file.name,
      size: file.size,
    }),
  )

  return [...uploaded, ...pending] as Array<PlanAttachment | PendingAttachment>
})

const allComments = computed(() => {
  const loaded = comments.value || []
  const pending = pendingComments.value.map((comment) => ({
    id: undefined,
    body: comment.body,
    createdAt: new Date().toISOString(),
    user: {
      forename: (user.value as { forename?: string })?.forename,
      surname: (user.value as { surname?: string })?.surname,
      username: (user.value as { username?: string })?.username || 'User',
    },
    replies: [],
  }))

  return [...loaded, ...pending]
})

function formatFileSize(bytes: number) {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`
}

async function onFilesUpload(e: { files: File | File[] }) {
  const files: File[] = Array.isArray(e.files) ? e.files : [e.files]
  const maxFileSize = 50 * 1024 * 1024

  const oversizedFiles = files.filter((file) => file.size > maxFileSize)
  if (oversizedFiles.length > 0) {
    const fileNames = oversizedFiles.map((f) => f.name).join(', ')
    toast(
      'File Too Large',
      `The following file(s) exceed the 50MB limit: ${fileNames}. Please choose smaller files.`,
      'error',
    )
    return
  }

  uploading.value = true
  try {
    if (form.id) {
      const uploadResults: Array<{ success: boolean; file: File; error?: string }> = []

      for (const file of files) {
        try {
          const uploaded = await uploadPlanAttachment(form.id, file)
          localAttachments.value = [...localAttachments.value, uploaded]
          uploadResults.push({ success: true, file })
        } catch (error: unknown) {
          const errorMessage = getUploadErrorMessage(error, file.name)
          uploadResults.push({ success: false, file, error: errorMessage })
          toast('Upload Failed', `Failed to upload "${file.name}": ${errorMessage}`, 'error')
        }
      }

      const failedCount = uploadResults.filter((r) => !r.success).length
      if (failedCount > 0 && failedCount < files.length) {
        toast(
          'Partial Upload',
          `${files.length - failedCount} file(s) uploaded successfully, ${failedCount} failed.`,
          'warn',
        )
      }
    } else {
      pendingAttachments.value.push(...files)
      toast(
        'Files Added',
        `${files.length} file(s) will be uploaded when you save the plan.`,
        'info',
      )
    }
  } catch (error: unknown) {
    const errorMessage = getUploadErrorMessage(error, 'files')
    toast('Upload Error', `Failed to process files: ${errorMessage}`, 'error')
  } finally {
    uploading.value = false
  }
}

function getUploadErrorMessage(error: unknown, fileName: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as {
      response?: {
        status?: number
        data?: { errors?: Record<string, string[]>; message?: string }
      }
    }
    const status = axiosError.response?.status
    const data = axiosError.response?.data

    if (status === 413 || status === 422) {
      if (data?.errors?.file) {
        return `${fileName}: ${data.errors.file.join(', ')}`
      }
      if (data?.message) {
        return `${fileName}: ${data.message}`
      }
      return `${fileName}: File size exceeds the maximum allowed limit (50MB)`
    }
    if (status === 415) {
      return `${fileName}: File type not allowed`
    }
    if (status === 500) {
      return `${fileName}: Server error occurred during upload`
    }
    if (data?.message) {
      return `${fileName}: ${data.message}`
    }
  } else if (error && typeof error === 'object' && 'request' in error) {
    return `${fileName}: Network error - please check your connection`
  } else if (error instanceof Error) {
    return `${fileName}: ${error.message}`
  }
  return `${fileName}: Unknown error occurred`
}

async function downloadAttachment(att: PlanAttachment | PendingAttachment) {
  if (!form.id || !att.id) return

  try {
    const fileName = att.originalName || (att as { name?: string }).name || 'attachment'
    await downloadPlanAttachment(form.id, att.id, fileName)
    toast('Download Started', `"${fileName}" is being downloaded.`, 'success')
  } catch (error: unknown) {
    const errorMessage =
      error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { data?: { message?: string } } }).response?.data?.message ||
          'Failed to download attachment'
        : error instanceof Error
          ? error.message
          : 'Failed to download attachment'
    toast('Error', `Failed to download attachment: ${errorMessage}`, 'error')
  }
}

async function removeAttachment(att: PlanAttachment | PendingAttachment, index?: number) {
  if (form.id && att.id) {
    try {
      await deletePlanAttachment(form.id, att.id)
      localAttachments.value = localAttachments.value.filter((a) => a.id !== att.id)
      toast('Attachment Removed', 'The attachment has been removed successfully.', 'success')
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message ||
            'Failed to remove attachment'
          : error instanceof Error
            ? error.message
            : 'Failed to remove attachment'
      toast('Error', `Failed to remove attachment: ${errorMessage}`, 'error')
    }
  } else if (index !== undefined) {
    const removedFile = pendingAttachments.value[index]
    pendingAttachments.value.splice(index, 1)
    toast('File Removed', `"${removedFile.name}" has been removed from the upload queue.`, 'info')
  } else {
    const fileName =
      (att as { name?: string; originalName?: string }).name ||
      (att as { name?: string; originalName?: string }).originalName
    if (fileName) {
      const fileIndex = pendingAttachments.value.findIndex((f) => f.name === fileName)
      if (fileIndex >= 0) {
        pendingAttachments.value.splice(fileIndex, 1)
        toast('File Removed', `"${fileName}" has been removed from the upload queue.`, 'info')
      }
    }
  }
}

async function postNewComment() {
  if (!newComment.value.trim()) return

  if (form.id) {
    const created = await postPlanComment(form.id, newComment.value.trim())
    comments.value.push(created)
  } else {
    pendingComments.value.push({ body: newComment.value.trim() })
  }

  newComment.value = ''
}

async function postReply(parentId: string | number) {
  const body = (replyBuffer.value[parentId] || '').trim()
  if (!body) return

  if (form.id) {
    const created = await postPlanComment(form.id, body, parentId)
    const parent = comments.value.find((c) => c.id === parentId)
    if (parent) {
      parent.replies = parent.replies || []
      parent.replies.push(created)
    }
  } else {
    const parent = allComments.value.find((c) => c.id === parentId)
    if (parent) {
      if (!parent.replies) {
        parent.replies = []
      }
      const pendingReply = {
        id: undefined as unknown as number | string,
        body,
        createdAt: new Date().toISOString(),
      } as PlanComment
      ;(parent.replies as PlanComment[]).push(pendingReply)
      pendingComments.value.push({ body, parentId })
    }
  }

  replyBuffer.value[parentId] = ''
}

function cancel() {
  emit('update:modelValue', false)
  emit('cancel')
}

async function submit() {
  if (!validateForm()) {
    showValidationErrors()
    return
  }

  const retailers: Retailer[] = []
  const groups: Group[] = []

  if (assignmentType.value === 'single-retailer' && form.singleRetailer) {
    const retailerCode = form.singleRetailer.code || form.singleRetailer.bank_code || ''
    retailers.push({
      id: form.singleRetailer.id,
      label: form.singleRetailer.label,
      code: retailerCode,
      bank_code: form.singleRetailer.bank_code,
      users: [],
    })
  } else if (assignmentType.value === 'multiple-retailers' && form.multipleRetailers.length > 0) {
    const selectedRetailers = form.multipleRetailers
      .map((id) => {
        return (
          selectedRetailersCache.value.get(id) || retailerSuggestions.value.find((r) => r.id === id)
        )
      })
      .filter((r): r is SelectOption => r !== undefined)

    retailers.push(
      ...selectedRetailers.map((r) => ({
        id: r.id,
        label: r.label,
        code: r.code || '',
        bank_code: r.bank_code,
        users: [],
      })),
    )
  } else if (assignmentType.value === 'group' && form.group) {
    groups.push({
      id: form.group.id,
      label: form.group.label,
      code: form.group.code || '',
    })

    if (form.groupRetailers.length > 0) {
      const selectedRetailers = form.groupRetailers
        .map((id) => {
          return (
            selectedRetailersCache.value.get(id) ||
            retailerSuggestions.value.find((r) => r.id === id)
          )
        })
        .filter((r): r is SelectOption => r !== undefined)

      retailers.push(
        ...selectedRetailers.map((r) => ({
          id: r.id,
          label: r.label,
          code: r.code || '',
          bank_code: r.bank_code,
          users: [],
        })),
      )
    }
  }

  const payload: Plan = {
    id: form.id || 0,
    title: form.title.trim(),
    active: form.active,
    actions: props.value?.actions || [],
    retailers: retailers,
    groups: groups,
    attachments: localAttachments.value,
    comments: comments.value,
  }

  saving.value = true

  const pendingAttachmentErrors: Array<{ file: File; error: string }> = []
  const pendingCommentErrors: Array<{ body: string; error: string }> = []

  try {
    const savedPlan = form.id ? await updatePlan(payload) : await createPlan(payload)
    const savedPlanId = savedPlan.id

    if (pendingAttachments.value.length > 0) {
      for (const file of pendingAttachments.value) {
        try {
          await uploadPlanAttachment(savedPlanId, file)
        } catch (error: unknown) {
          const errorMessage = getUploadErrorMessage(error, file.name)
          pendingAttachmentErrors.push({ file, error: errorMessage })
          console.error(`Failed to upload plan attachment "${file.name}":`, error)
        }
      }
    }

    if (pendingComments.value.length > 0) {
      for (const comment of pendingComments.value) {
        try {
          await postPlanComment(savedPlanId, comment.body, comment.parentId)
        } catch (error: unknown) {
          const errorMessage =
            error && typeof error === 'object' && 'response' in error
              ? (error as { response?: { data?: { message?: string } } }).response?.data
                  ?.message || 'Failed to post comment'
              : error instanceof Error
                ? error.message
                : 'Failed to post comment'
          pendingCommentErrors.push({ body: comment.body, error: errorMessage })
          console.error('Failed to post plan comment:', error)
        }
      }
    }

    const [savedComments, savedAttachments] = await Promise.all([
      fetchPlanComments(savedPlanId).catch(() => savedPlan.comments || []),
      fetchPlanAttachments(savedPlanId).catch(() => savedPlan.attachments || []),
    ])

    const updatedPlan: Plan = {
      ...savedPlan,
      actions: payload.actions,
      retailers: payload.retailers,
      groups: payload.groups,
      attachments: savedAttachments,
      comments: savedComments,
    }

    pendingAttachments.value = pendingAttachments.value.filter(
      (file) => !pendingAttachmentErrors.some((error) => error.file.name === file.name),
    )
    pendingComments.value = pendingComments.value.filter(
      (comment) => !pendingCommentErrors.some((error) => error.body === comment.body),
    )

    if (pendingAttachmentErrors.length > 0) {
      toast(
        'Some Attachments Failed',
        `${pendingAttachmentErrors.length} attachment(s) failed to upload after saving the plan.`,
        'warn',
      )
    }

    if (pendingCommentErrors.length > 0) {
      toast(
        'Some Comments Failed',
        `${pendingCommentErrors.length} comment(s) failed to post after saving the plan.`,
        'warn',
      )
    }

    if (pendingAttachmentErrors.length === 0) {
      pendingAttachments.value = []
    }
    if (pendingCommentErrors.length === 0) {
      pendingComments.value = []
    }

    comments.value = savedComments
    localAttachments.value = savedAttachments

    emit('save', updatedPlan)
    emit('update:modelValue', false)
  } catch (error: unknown) {
    let errorMessage = form.id
      ? 'Failed to update plan. Please try again.'
      : 'Failed to create plan. Please try again.'
    let errorTitle = form.id ? 'Error Updating Plan' : 'Error Creating Plan'

    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as {
        response?: {
          status?: number
          data?: { errors?: Record<string, string[]>; message?: string }
        }
      }
      const status = axiosError.response?.status
      const data = axiosError.response?.data

      if (status === 422) {
        errorTitle = 'Validation Error'
        if (data?.errors && typeof data.errors === 'object') {
          const errorMessages = Object.values(data.errors).flat() as string[]
          errorMessage = errorMessages.join(', ') || 'Please check your input and try again.'
        } else if (data?.message) {
          errorMessage = data.message
        }
      } else if (status === 401) {
        errorTitle = 'Authentication Error'
        errorMessage = 'Your session has expired. Please refresh the page and try again.'
      } else if (status === 403) {
        errorTitle = 'Permission Denied'
        errorMessage = 'You do not have permission to save this plan.'
      } else if (status === 404) {
        errorTitle = 'Not Found'
        errorMessage = form.id
          ? 'The plan you are trying to update could not be found.'
          : 'The requested resource could not be found.'
      } else if (status && status >= 500) {
        errorTitle = 'Server Error'
        errorMessage = 'An error occurred on the server. Please try again later.'
      } else if (data?.message) {
        errorMessage = data.message
      }
    } else if (error && typeof error === 'object' && 'request' in error) {
      errorTitle = 'Network Error'
      errorMessage =
        'Unable to connect to the server. Please check your internet connection and try again.'
    } else if (error instanceof Error) {
      errorMessage = error.message
    }

    toast(errorTitle, errorMessage, 'error')
    saving.value = false
  }
}

async function duplicate() {
  if (!props.value?.id) return

  duplicating.value = true
  try {
    const duplicatedPlan = await duplicatePlan(props.value.id)
    emit('editDuplicated', duplicatedPlan)
    toast('Plan Duplicated', `Plan "${duplicatedPlan.title}" has been created successfully.`)
  } catch {
    toast('Error', 'Failed to duplicate plan. Please try again.', 'error')
  } finally {
    duplicating.value = false
  }
}

function confirmDelete() {
  showDeleteConfirm.value = true
}

async function handleDelete() {
  if (!props.value?.id) return

  deleting.value = true
  try {
    await deletePlan(props.value.id)
    emit('deleted', props.value.id)
    emit('update:modelValue', false)
    toast('Plan Deleted', `Plan "${props.value.title}" has been deleted successfully.`, 'success')
  } catch {
    toast('Error', 'Failed to delete plan. Please try again.', 'error')
  } finally {
    deleting.value = false
    showDeleteConfirm.value = false
  }
}

async function loadHistory() {
  if (!isEdit.value || !props.value?.id) {
    planHistory.value = []
    return
  }

  historyLoading.value = true
  historyError.value = null

  try {
    const planId = Number(props.value.id)

    const planLogsResponse = await fetchAuditLogs({
      model_type: 'Plan',
      model_id: planId,
      per_page: 100,
      page: 1,
    })

    const actionIds = props.value.actions?.map((action) => Number(action.id)) || []

    let actionLogsResponse: { data: AuditLog[] } = { data: [] }
    if (actionIds.length > 0) {
      const batchSize = 10
      const batches: number[][] = []
      for (let i = 0; i < actionIds.length; i += batchSize) {
        batches.push(actionIds.slice(i, i + batchSize))
      }

      const allActionLogs: AuditLog[] = []
      for (const batch of batches) {
        const batchPromises = batch.map((actionId) =>
          fetchAuditLogs({
            model_type: 'Action',
            model_id: actionId,
            per_page: 100,
            page: 1,
          })
            .then((result) => result.data || [])
            .catch(() => []),
        )
        const batchResults = await Promise.all(batchPromises)
        allActionLogs.push(...batchResults.flat())
      }

      actionLogsResponse = { data: allActionLogs }
    }

    const allLogs = [...(planLogsResponse.data || []), ...(actionLogsResponse.data || [])]

    planHistory.value = allLogs.sort((a, b) => {
      const dateA = new Date(a.created_at).getTime()
      const dateB = new Date(b.created_at).getTime()
      return dateB - dateA
    })
  } catch (error) {
    console.error('Failed to load plan history:', error)
    historyError.value = 'Failed to load plan history. Please try again.'
    planHistory.value = []
  } finally {
    historyLoading.value = false
  }
}

const { getStateIcon, getStateBadgeClass, getStateLabel, formatActionDate, getKpiAreaColorClass } =
  useActions()

const isOverdue = isActionOverdue

function handleActionClick(action: Action) {
  emit('action:click', action)
}

function handleCreateAction() {
  if (props.value?.id) {
    emit('action:create', props.value.id)
  }
}
</script>

<style scoped>
:deep(.p-tabpanels) {
  padding: 0;
  min-height: 300px;
}

@media (min-width: 640px) {
  :deep(.p-tabpanels) {
    min-height: 400px;
  }
}

:deep(.p-tablist) {
  margin-bottom: 1rem;
}

:deep(.p-tab) {
  padding: 0.625rem 0.75rem;
  font-size: 0.875rem;
}

@media (min-width: 640px) {
  :deep(.p-tab) {
    padding: 0.75rem 1rem;
    font-size: 0.9375rem;
  }
}

.multiselect-wrap-chips :deep(.p-multiselect-label) {
  margin: 0;
  flex-shrink: 0;
  flex-wrap: wrap;
}

/* Mobile improvements for Select and MultiSelect */
@media (max-width: 639px) {
  :deep(.p-select),
  :deep(.p-multiselect) {
    min-height: 44px;
  }

  :deep(.p-select-label),
  :deep(.p-multiselect-label) {
    padding: 0.75rem;
    font-size: 0.875rem;
  }

  :deep(.p-select-panel),
  :deep(.p-multiselect-panel) {
    width: calc(100vw - 2rem) !important;
    max-width: 400px !important;
  }

  :deep(.p-select-items-wrapper),
  :deep(.p-multiselect-items-wrapper) {
    max-height: 50vh;
  }
}

/* Mobile improvements for SelectButton */
@media (max-width: 639px) {
  :deep(.p-selectbutton .p-button) {
    min-height: 44px;
    padding: 0.625rem 0.75rem;
    font-size: 0.8125rem;
  }
}

/* Mobile improvements for ToggleSwitch */
@media (max-width: 639px) {
  :deep(.p-toggleswitch) {
    width: 3rem;
    height: 1.75rem;
  }

  :deep(.p-toggleswitch-slider) {
    width: 1.5rem;
    height: 1.5rem;
  }
}

.diff-old pre,
.diff-new pre,
.timeline-new-values pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.7rem;
  line-height: 1.4;
}

.a11y-reduced-motion :deep(.p-dialog) {
  transition: none !important;
}

.a11y-reduced-motion .bg-\(--p-surface-50\) {
  transition: none !important;
}

/* Mobile footer button improvements */
@media (max-width: 639px) {
  :deep(.p-dialog-footer) {
    padding: 1rem;
    border-top: 1px solid var(--p-surface-200);
    background: var(--p-surface-0);
  }

  :deep(.p-dialog-footer .p-button) {
    font-size: 0.9375rem;
    letter-spacing: 0.01em;
  }

  :deep(.p-dialog-footer .p-button.p-button-primary) {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  :deep(.p-dialog-footer .p-button:active) {
    transform: scale(0.98);
  }
}
</style>
